import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { CONFIG } from 'src/global-config';

import { JWT_STORAGE_KEY } from 'src/auth/context/jwt/constant';
import { clearAuthFromOutside } from 'src/auth/context/jwt/auth-ref';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: CONFIG.swaggerUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem(JWT_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url;

    // 401 인증 실패 처리
    if (status === 401) {
      // 로그인 페이지 요청이 아닌 경우에만 로그아웃 처리
      if (!url?.includes('/auth/sign-in')) {
        clearAuthFromOutside();

        // 현재 페이지가 로그인 페이지가 아닌 경우에만 리다이렉트
        if (window.location.pathname !== '/auth/sign-in') {
          window.location.href = '/auth/sign-in';
        }
      }
      throw error;
    }

    console.error('Axios error:', error);

    throw error;
  }
);

export default axiosInstance;

export const axiosFormData = axios.create({
  baseURL: CONFIG.swaggerUrl,
  timeout: 10000,
});

axiosFormData.interceptors.request.use(
  (config) => {
    const token = window.localStorage.getItem(JWT_STORAGE_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    throw error;
  }
);

axiosFormData.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url;

    if (status === 401) {
      if (!url?.includes('/auth/sign-in')) {
        clearAuthFromOutside();

        if (window.location.pathname !== '/auth/sign-in') {
          window.location.href = '/auth/sign-in';
        }
      }
      throw error;
    }

    throw error;
  }
);

// ----------------------------------------------------------------------

export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    const res = await axiosInstance.get<T>(url, config);

    return res.data;
  } catch (error) {
    console.error('Fetcher failed:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    // me: '/jwt/verify/',
    // me: '/accounts/user/',
    // signIn: '/jwt/token/',
    signIn: '/accounts/token/', // 로그인 반영 완료
    signUp: '/auth/sign-up', // 회원가입 기능X
  },
  linkserver: {
    status: '/linkserver/status/',
  },
  permission: {
    me: '/accounts/user/me/permissions/', // 현재 사용자 메뉴 권한
  },
  centerUsage: {
    settop: '/center/settop/usage/',
    healthcare: '/center/healthcare/usage/',
    lifecare: '/center/lifecare/usage/',
  },
  monitoring: {
    dashboard: {
      /** @deprecated */
      old_statistics: '/old_api/dashboard/',
      statistics: '/statistic/top/', // 반영 완료
      analytics: '/statistic/dashboard/analytics/', // 종합 대시보드 (하단)
      /** @deprecated */
      lifecareOnOff: '/old_api/lifecare/center/total/', // api 없음
      service: '/statistic/service/',
      /** @deprecated */
      issue: '/old_api/statistics/issue/center/', // api 없음
      cumulativeUsage: '/statistic/dashboard/cumulative-usage/',
      districtUsage: '/statistic/dashboard/district-usage/',
    },
    center: {
      statistics: '/statistic/settop/',
      detail: '/statistic/',
    },
    healthcare: '/healthcare/',
    healthcareStatistics: '/healthcare/statistics/',
    healthcareUsageLog: '/healthcare/usage-log/',
    lifecare: '/lifecare/',
    lifecareStatistics: '/lifecare/statistics/',
  },
  notice: {
    category: 'notice/category/',
    notice: '/notice/',
    template: '/notice/template/',
    linked: '/notice/linked/',
  },
  broadcast: {
    schedule: {
      category: '/broadcasting/category/',
      schedules: '/broadcasting/',
    },
    vod: {
      category: '/vod/category/',
      list: '/vod/',
      create: '/vod/create/',
      upload: '/vod/upload/',
      server: {
        index: '/vod/server/',
        insert: '/vod/server/import/',
      },
    },
    server: {
      list: '/broadcasting/server/',
    },
    import: {
      server: '/broadcasting/import/server/', // 외부 편성표 등록하기
    },
    list: {
      server: '/broadcasting/list/server/', // 외부 편성표 불러오기
    },
    batchCopy: '/broadcasting/batch-copy/', // 연계 편성표 일괄 복사
    approval: {
      list: '/broadcasting/approval/', // 긴급 방송 승인 목록
      action: (scheduleId: number) => `/broadcasting/approval/${scheduleId}/`, // 긴급 방송 승인/반려
    },
    fallback: '/broadcasting/fallback/', // 자동 복구 편성
    viewingTimeStats: '/broadcasting/viewing-time-stats/', // 일일 시청시간 통계
  },
  management: {
    authority: '/accounts/authority/', // Simple list for dropdowns
    authorityDetail: (id: number) => `/accounts/authority/${id}/`,
    accounts: {
      authority: '/accounts/authority/', // Paginated list
      user: '/accounts/user/', // Admin user management
      center: '/settop/',
      settopStats: '/settop/stats/',
      category: '/accounts/user/log/category/',
      log: '/accounts/user/log/',
      videoCall: '/video/',
      centerAccount: '/center/', // 경로당 계정 관리 (양방향 화상 시스템)
      healthLifecare: '/accounts/healthlifecare/', // 헬스&생활케어 계정
      healthLifecareDetail: (id: number) => `/accounts/healthlifecare/${id}`,
    },
    userProfiles: {
      list: '/msgclient/user-profiles/',
      detail: (id: number) => `/msgclient/user-profiles/${id}/`,
      approve: (id: number) => `/msgclient/user-profiles/${id}/approve/`,
      reject: (id: number) => `/msgclient/user-profiles/${id}/reject/`,
    },
  },
  restaurant: {
    list: '/restaurant/',
    detail: (id: number) => `/restaurant/${id}/`,
  },
  survey: {
    list: '/survey/',
    detail: (id: number) => `/survey/${id}/`,
    statistics: (id: number) => `/survey/${id}/statistics/`,
    submit: (id: number) => `/survey/${id}/submit/`,
    responses: (id: number) => `/survey/${id}/responses/`,
    response: (id: number) => `/survey/response/${id}/`,
    category: {
      list: '/survey/category/',
      detail: (id: number) => `/survey/category/${id}/`,
    },
  },
  issue: {
    list: '/issue/',
    detail: (id: number) => `/issue/${id}/`,
    category: '/issue/category/',
    subcategory: '/issue/subcategory/',
    solution: '/issue/solution/',
    riskLevel: '/issue/risklevel/',
    message: '/issue/message/',
    resolve: '/issue/resolve/',
    statics: '/issue/statics/',
  },
  helpdesk: {
    list: '/helpdesk/',
    unprocessed: '/helpdesk/unprocessed/',
    detail: (id: number) => `/helpdesk/${id}/`,
    category: '/helpdesk/category/',
  },
  lifecare: {
    /** @deprecated */
    competition: '/old_api/lifecare/walking/competition/',
    /** @deprecated */
    competitionHistory: (id: number) => `/old_api/lifecare/walking/competition/${id}/history/`,
    /** @deprecated */
    courses: '/old_api/lifecare/walking/courses/',
    /** @deprecated */
    courseRanking: '/old_api/lifecare/walking/ranking/',
    // Smart Table
    smartTableContents: '/old_api/lifecare/table/contents/',
    smartTableRanking: '/old_api/lifecare/table/ranking/',
  },
  busStation: {
    search: '/utility/bus-station/search/',
  },
  integratedLog: {
    list: '/old_api/logs/',
    detail: (id: number) => `/old_api/logs/${id}/`,
  },
  accountRequest: {
    download: '/accounts/user/request/',
    upload: '/accounts/user/request/upload/',
  },
  statistic: {
    high: '/statistic/high/',
    issue: '/statistic/issue/',
    heatmap: '/statistic/heatmap/',
  },
  promotional: {
    material: '/promotional/',
  },
  linkedBroadcastViewing: {
    /** @deprecated */
    list: '/broadcasting/linked-viewing/',
    detail: (id: number) => `/broadcasting/linked-viewing/${id}/`,
  },
} as const;
